import { ChangeDetectionStrategy, Component, input, model, OnInit, ViewEncapsulation } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { StylesUtil } from "./s25.styles.util";

@Component({
    standalone: true,
    selector: "s25-ng-css-rule",
    template: `
        @for (property of rule; track property) {
            <div class="wrapper">
                <div class="property" [class.invalid]="!!errors[$index]">
                    <select [(ngModel)]="property.property" (ngModelChange)="onChange()">
                        @for (property of allowedProperties(); track property) {
                            <option [value]="property">{{ property }}</option>
                        }
                    </select>
                    <span class="colon">:</span>
                    <input type="text" [(ngModel)]="property.value" (ngModelChange)="onChange()" />
                    <span class="semiColon">;</span>
                    <button class="delete dot" (click)="delete($index)">
                        <s25-ng-icon [type]="'minus'" [label]="'Remove property'" />
                    </button>
                </div>
                @if (errors[$index]) {
                    <span class="error ngRed">{{ errors[$index] }}</span>
                }
            </div>
        }

        <button class="add dot" (click)="add()">
            <s25-ng-icon [type]="'plus'" [label]="'Add property'" />
        </button>
    `,
    styles: `
        :host {
            --green: #60a725;
            --red: #d62000;
            --border-color: black;
            --color: black;

            display: grid;
            row-gap: 0.5rem;
            grid-template-columns: 100%;
        }

        ::ng-deep body.nm-party--on s25-ng-css-rule {
            --border-color: white;
            --color: white;
        }

        .property {
            display: flex;
        }

        .property > * {
            min-width: 0;
        }

        .colon,
        .semiColon {
            padding: 1px;
        }

        .colon {
            padding-right: 0.5rem;
        }

        select {
            align-items: center;
            background-color: var(--cn-blue);
            border-radius: 3px;
            color: white !important;
            border: 0;
            padding-block: 0.2em;
        }

        input {
            border: 0;
            border-bottom: 1px dotted var(--border-color);
            padding-inline-start: 0.25rem;
            background-color: transparent;
            color: var(--color);
        }

        button {
            padding: 0;
            border: 0;
            cursor: pointer;
        }

        .dot {
            background-color: var(--dot-color);
            border-radius: 50%;
            flex-shrink: 0;
            color: white;
            aspect-ratio: 1;
            font-size: 0.6em;
            height: 60%;
            align-self: center;
            margin-left: 0.5em;
            position: relative;

            s25-ng-icon {
                top: 50%;
                left: 50%;
                translate: -50% -50%;
                font-size: 0.6rem;
                color: white;
                position: absolute;
            }
        }

        .delete {
            --dot-color: var(--red);
        }

        .add {
            --dot-color: var(--green);
            justify-self: center;
            height: 1.5rem;

            s25-ng-icon {
                font-size: 1.5em;
            }
        }

        .wrapper {
            display: flex;
            flex-direction: column;
        }

        .invalid input {
            border-color: var(--conflict-color) !important;
            background: color-mix(in hsl, var(--conflict-color), transparent 90%);
        }

        .error {
            font-size: 0.9em;
            display: block;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [FormsModule, S25DropdownPaginatedModule, S25IconComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CssRuleComponent implements OnInit {
    styles = model.required<string>();
    allowedProperties = input.required<string[]>();
    validateProperty = input<StylesUtil.CssPropertyValidator>();
    valid = model<boolean>(true);

    rule: StylesUtil.CssRule = [];
    errors: string[] = [];

    ngOnInit() {
        this.rule = StylesUtil.parseCssRule(this.styles());
        this.validate();
    }

    onChange() {
        this.validate();
        this.stringify();
    }

    add() {
        this.rule.push({ property: "", value: "" });
        this.onChange();
    }

    delete(index: number) {
        this.rule.splice(index, 1);
        this.onChange();
    }

    stringify() {
        this.styles.set(StylesUtil.stringifyCssRule(this.rule));
    }

    validate() {
        this.valid.set(true);
        this.errors = StylesUtil.validateCssRule(this.rule, this.validateProperty()).error ?? [];
        if (this.errors.length) this.valid.set(false);
    }
}
